import {
  createCompany,
  deleteCompany,
  getCompanyRegistryDetails,
  listCompanySummaries,
  searchCompanyRegistry,
  updateCompany,
  type CoworkCompanyRegistrySuggestion,
  type CoworkCompanySummary,
} from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useLanguage } from "@/context/language"
import { DialogCoworkDeleteCompany } from "@/pages/home/cowork-project-dialogs"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { useAppNavigate } from "@/cowork/workspace-route"
import { createMemo, createResource, createUniqueId, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkCompaniesPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const navigate = useAppNavigate()
  const dialog = useDialog()
  const [companies, companyActions] = createResource(listCompanySummaries)
  const [state, setState] = createStore({
    search: "",
    formOpen: false,
    editingID: "",
    name: "",
    country: "",
    registrationNumber: "",
    vatNumber: "",
    suggestions: [] as CoworkCompanyRegistrySuggestion[],
    suggestionsOpen: false,
    activeSuggestion: -1,
    selectedName: "",
    registryStatus: "" as "" | "registryLoading" | "registryEmpty" | "registryUnavailable",
    detailsStatus: "" as "" | "registryDetailsLoading" | "registryDetailsUnavailable" | "registryVatUnavailable",
    saving: false,
    deleting: "",
    error: "",
  })
  const formID = createUniqueId()
  let nameInput: HTMLInputElement | undefined
  let suggestionList: HTMLUListElement | undefined
  let searchTimer: ReturnType<typeof setTimeout> | undefined
  let searchController: AbortController | undefined
  let detailsController: AbortController | undefined
  let searchGeneration = 0
  let detailsGeneration = 0
  let disposed = false

  const closeSuggestions = () => {
    clearTimeout(searchTimer)
    searchController?.abort()
    searchGeneration++
    setState({ suggestions: [], suggestionsOpen: false, activeSuggestion: -1, registryStatus: "" })
  }

  const cancelDetails = () => {
    detailsController?.abort()
    detailsGeneration++
    setState("detailsStatus", "")
  }

  onCleanup(() => {
    disposed = true
    clearTimeout(searchTimer)
    searchController?.abort()
    detailsController?.abort()
  })

  const searchRegistry = () => {
    closeSuggestions()
    const name = state.name
    const query = name.trim()
    if (
      disposed ||
      !state.formOpen ||
      state.editingID ||
      state.saving ||
      query.length < 2 ||
      query.length > 200 ||
      name === state.selectedName
    )
      return
    const generation = searchGeneration
    const controller = new AbortController()
    searchController = controller
    const current = () =>
      !disposed &&
      !controller.signal.aborted &&
      generation === searchGeneration &&
      state.formOpen &&
      !state.editingID &&
      !state.saving &&
      state.name === name
    setState("registryStatus", "registryLoading")
    searchTimer = setTimeout(async () => {
      try {
        const suggestions = (await searchCompanyRegistry(query, controller.signal)).slice(0, 10)
        if (!current()) return
        setState({
          suggestions,
          suggestionsOpen: suggestions.length > 0,
          activeSuggestion: -1,
          registryStatus: suggestions.length ? "" : "registryEmpty",
        })
      } catch {
        if (current()) setState("registryStatus", "registryUnavailable")
      }
    }, 300)
  }

  const selectSuggestion = async (suggestion: CoworkCompanyRegistrySuggestion) => {
    if (disposed || !state.formOpen || state.editingID || state.saving) return
    closeSuggestions()
    cancelDetails()
    const generation = detailsGeneration
    const controller = new AbortController()
    detailsController = controller
    const name = suggestion.name
    const registrationNumber = suggestion.registration_number
    setState({
      name,
      selectedName: name,
      registrationNumber,
      country: "EE",
      vatNumber: "",
      detailsStatus: "registryDetailsLoading",
    })
    nameInput?.focus()
    // Generation and field guards also protect against APIs that ignore cancellation.
    const current = () =>
      !disposed &&
      !controller.signal.aborted &&
      generation === detailsGeneration &&
      state.formOpen &&
      !state.editingID &&
      !state.saving &&
      state.name === name &&
      state.registrationNumber === registrationNumber &&
      state.country === "EE" &&
      state.vatNumber === ""
    try {
      const details = await getCompanyRegistryDetails(registrationNumber, controller.signal)
      if (!current()) return
      setState({
        name: details.name,
        selectedName: details.name,
        registrationNumber: details.registration_number,
        vatNumber: details.vat_number ?? "",
        detailsStatus: details.vat_number ? "" : "registryVatUnavailable",
      })
    } catch {
      if (current()) setState("detailsStatus", "registryDetailsUnavailable")
    }
  }

  const registryMessage = createMemo(() => {
    const status = state.detailsStatus || state.registryStatus
    if (status) return language.t(`cowork.enterprise.companies.${status}`)
    if (state.suggestionsOpen) return language.t("cowork.enterprise.companies.registrySuggestions")
    return language.t("cowork.enterprise.companies.registryHint")
  })
  const filtered = createMemo(() => {
    const query = state.search.trim().toLowerCase()
    if (!query) return companies() ?? []
    return (companies() ?? []).filter((company) =>
      `${company.name} ${company.country ?? ""} ${company.status}`.toLowerCase().includes(query),
    )
  })

  const openCompany = (company: CoworkCompanySummary) => {
    context.selectEntity(company.id)
    context.selectProject("")
    navigate(`/companies/${company.id}/overview`)
  }

  const editCompany = (company: CoworkCompanySummary) => {
    if (disposed || state.saving) return
    closeSuggestions()
    cancelDetails()
    setState({
      formOpen: true,
      editingID: company.id,
      name: company.name,
      country: company.country || "",
      registrationNumber: company.registration_number || "",
      vatNumber: company.vat_number || "",
      selectedName: "",
      error: "",
    })
  }

  const resetForm = () => {
    if (disposed || state.saving) return
    closeSuggestions()
    cancelDetails()
    setState({
      formOpen: false,
      editingID: "",
      name: "",
      country: "",
      registrationNumber: "",
      vatNumber: "",
      selectedName: "",
      error: "",
    })
  }

  const saveCompany = async (event: SubmitEvent) => {
    event.preventDefault()
    if (
      disposed ||
      !state.formOpen ||
      !state.name.trim() ||
      state.saving ||
      state.detailsStatus === "registryDetailsLoading"
    )
      return
    const editing = !!state.editingID
    closeSuggestions()
    setState({ saving: true, error: "" })
    try {
      const input = {
        name: state.name.trim(),
        country: state.country.trim() || undefined,
        registration_number: state.registrationNumber.trim() || undefined,
        vat_number: state.vatNumber.trim() || null,
      }
      const company = editing ? await updateCompany(state.editingID, input) : await createCompany(input)
      if (disposed) return
      await Promise.all([companyActions.refetch(), context.refresh()])
      if (disposed) return
      setState("saving", false)
      resetForm()
      if (!editing) openCompany(company)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (!disposed)
        setState(
          "error",
          message === "trial_company_limit" ? language.t("cowork.enterprise.companies.trialLimit") : message,
        )
    } finally {
      if (!disposed) setState("saving", false)
    }
  }

  const removeCompany = async (company: CoworkCompanySummary) => {
    if (disposed || state.deleting || state.saving) return
    setState({ deleting: company.id, error: "" })
    try {
      await deleteCompany(company.id)
      if (disposed) return
      if (context.state.entityId === company.id) {
        context.selectEntity((companies() ?? []).find((item) => item.id !== company.id)?.id ?? "")
      }
      await Promise.all([companyActions.refetch(), context.refresh()])
    } catch (error) {
      if (!disposed) setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      if (!disposed) setState("deleting", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1500px]">
        <header class="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em] text-v2-text-text-base">
              {language.t("cowork.enterprise.nav.companies")}
            </h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.companies.description")}
            </p>
          </div>
          <div class="flex min-w-0 items-center gap-2">
            <label class="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 sm:w-[260px]">
              <SearchIcon />
              <input
                class="min-w-0 flex-1 bg-transparent text-[13px] text-v2-text-text-base outline-none placeholder:text-v2-text-text-faint"
                aria-label={language.t("cowork.enterprise.companies.searchLabel")}
                placeholder={language.t("cowork.enterprise.companies.search")}
                value={state.search}
                onInput={(event) => setState("search", event.currentTarget.value)}
              />
            </label>
            <button
              type="button"
              class="h-10 shrink-0 rounded-lg bg-v2-background-bg-contrast px-4 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
              disabled={state.saving}
              onClick={() => {
                if (disposed || state.saving) return
                resetForm()
                setState("formOpen", true)
              }}
            >
              {language.t("cowork.enterprise.companies.add")}
            </button>
          </div>
        </header>
        <Show when={state.error && !state.formOpen}>
          <div class="mt-4 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger">
            {state.error}
          </div>
        </Show>

        <Show when={state.formOpen}>
          <form
            class="mt-6 grid gap-3 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 md:grid-cols-2 xl:grid-cols-4"
            onSubmit={saveCompany}
          >
            <div
              class="relative min-w-0"
              onFocusOut={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closeSuggestions()
              }}
            >
              <label for={`${formID}-name`} class="mb-1.5 block text-[12px] text-v2-text-text-muted">
                {language.t("cowork.enterprise.companies.company")}
              </label>
              <input
                ref={nameInput}
                id={`${formID}-name`}
                required
                maxLength={255}
                readOnly={state.saving}
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
                role={state.editingID ? undefined : "combobox"}
                aria-expanded={state.editingID ? undefined : state.suggestionsOpen}
                aria-controls={state.editingID ? undefined : `${formID}-suggestions`}
                aria-autocomplete={state.editingID ? undefined : "list"}
                aria-activedescendant={
                  state.suggestionsOpen && state.activeSuggestion >= 0
                    ? `${formID}-option-${state.activeSuggestion}`
                    : undefined
                }
                aria-describedby={state.editingID ? undefined : `${formID}-registry-status`}
                autocomplete="off"
                placeholder={language.t("cowork.enterprise.companies.namePlaceholder")}
                value={state.name}
                onInput={(event) => {
                  cancelDetails()
                  setState({ name: event.currentTarget.value, selectedName: "" })
                  searchRegistry()
                }}
                onFocus={searchRegistry}
                onKeyDown={(event) => {
                  if (state.editingID || state.saving || event.isComposing) return
                  if (event.key === "Escape") {
                    event.preventDefault()
                    closeSuggestions()
                    return
                  }
                  if (event.key === "Enter" && state.suggestionsOpen) {
                    event.preventDefault()
                    const suggestion = state.suggestions[state.activeSuggestion]
                    if (suggestion) void selectSuggestion(suggestion)
                    return
                  }
                  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return
                  event.preventDefault()
                  if (!state.suggestionsOpen) {
                    searchRegistry()
                    return
                  }
                  const count = state.suggestions.length
                  const index =
                    state.activeSuggestion < 0
                      ? event.key === "ArrowDown"
                        ? 0
                        : count - 1
                      : (state.activeSuggestion + (event.key === "ArrowDown" ? 1 : -1) + count) % count
                  setState("activeSuggestion", index)
                  suggestionList?.children[index]?.scrollIntoView({ block: "nearest" })
                }}
              />
              <Show when={!state.editingID && state.suggestionsOpen}>
                <ul
                  ref={suggestionList}
                  id={`${formID}-suggestions`}
                  role="listbox"
                  aria-label={language.t("cowork.enterprise.companies.registrySuggestions")}
                  class="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto overscroll-contain rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 py-1 shadow-lg"
                >
                  <For each={state.suggestions}>
                    {(suggestion, index) => (
                      <li
                        id={`${formID}-option-${index()}`}
                        role="option"
                        aria-selected={state.activeSuggestion === index()}
                        class="cursor-pointer px-3 py-2 text-[12px] hover:bg-v2-overlay-simple-overlay-hover"
                        classList={{ "bg-v2-overlay-simple-overlay-hover": state.activeSuggestion === index() }}
                        onPointerDown={(event) => event.preventDefault()}
                        onPointerMove={() => setState("activeSuggestion", index())}
                        onClick={() => void selectSuggestion(suggestion)}
                      >
                        <span class="block break-words font-medium">{suggestion.name}</span>
                        <span class="block break-words text-[11px] text-v2-text-text-muted">
                          {suggestion.registration_number}
                        </span>
                        <Show when={suggestion.address}>
                          <span class="block break-words text-[11px] text-v2-text-text-muted">
                            {suggestion.address}
                          </span>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
            <label class="min-w-0 text-[12px] text-v2-text-text-muted">
              <span class="mb-1.5 block">{language.t("cowork.enterprise.companies.country")}</span>
              <input
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] text-v2-text-text-base outline-none"
                placeholder={language.t("cowork.enterprise.companies.countryPlaceholder")}
                readOnly={state.saving}
                value={state.country}
                onInput={(event) => {
                  cancelDetails()
                  setState("country", event.currentTarget.value)
                }}
              />
            </label>
            <label class="min-w-0 text-[12px] text-v2-text-text-muted">
              <span class="mb-1.5 block">{language.t("cowork.enterprise.companies.registrationNumber")}</span>
              <input
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] text-v2-text-text-base outline-none"
                placeholder={language.t("cowork.enterprise.companies.registrationNumber")}
                readOnly={state.saving}
                value={state.registrationNumber}
                onInput={(event) => {
                  cancelDetails()
                  setState("registrationNumber", event.currentTarget.value)
                }}
              />
            </label>
            <label class="min-w-0 text-[12px] text-v2-text-text-muted">
              <span class="mb-1.5 block">{language.t("cowork.enterprise.companies.vatNumber")}</span>
              <input
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] text-v2-text-text-base outline-none"
                placeholder={language.t("cowork.enterprise.companies.vatPlaceholder")}
                maxLength={128}
                readOnly={state.saving}
                value={state.vatNumber}
                onInput={(event) => {
                  cancelDetails()
                  setState("vatNumber", event.currentTarget.value)
                }}
              />
            </label>
            <Show when={!state.editingID}>
              <p
                id={`${formID}-registry-status`}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                class="col-span-full text-[11px] text-v2-text-text-muted"
              >
                {registryMessage()}
              </p>
            </Show>
            <Show when={state.error}>
              <p role="alert" class="col-span-full text-[11px] text-v2-state-fg-danger">
                {state.error}
              </p>
            </Show>
            <div class="col-span-full flex justify-end gap-2">
              <button
                type="button"
                class="rounded-md px-3 py-2 text-[11px] disabled:opacity-50"
                disabled={state.saving}
                onClick={resetForm}
              >
                {language.t("common.cancel")}
              </button>
              <button
                type="submit"
                class="rounded-md bg-v2-background-bg-contrast px-4 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                disabled={state.saving || state.detailsStatus === "registryDetailsLoading" || !state.name.trim()}
              >
                {state.saving
                  ? language.t("cowork.enterprise.settings.saving")
                  : language.t(state.editingID ? "common.save" : "cowork.enterprise.companies.create")}
              </button>
            </div>
          </form>
        </Show>

        <div class="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label={language.t("cowork.enterprise.companies.total")} value={companies()?.length ?? 0} />
          <SummaryCard
            label={language.t("cowork.enterprise.companies.projects")}
            value={(companies() ?? []).reduce((sum, item) => sum + item.projects, 0)}
          />
          <SummaryCard
            label={language.t("cowork.enterprise.nav.reports")}
            value={(companies() ?? []).reduce((sum, item) => sum + item.reports, 0)}
          />
          <SummaryCard
            label={language.t("cowork.enterprise.dashboard.activeWorkflows")}
            value={(companies() ?? []).reduce((sum, item) => sum + item.active_workflows, 0)}
          />
        </div>

        <section class="mt-6 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <div class="flex items-center justify-between border-b border-v2-border-border-base px-5 py-4">
            <div>
              <h2 class="text-[15px] font-semibold">{language.t("cowork.enterprise.companies.portfolio")}</h2>
              <p class="mt-0.5 text-[11px] text-v2-text-text-muted">
                {language.t("cowork.enterprise.companies.count", { count: filtered().length })}
              </p>
            </div>
          </div>
          <Show
            when={!companies.loading && filtered().length > 0}
            fallback={<EmptyState loading={companies.loading} text={language.t("cowork.enterprise.companies.empty")} />}
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[960px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.companies.company")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.companies.country")}</th>
                    <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.projects.title")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.navigation.files")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.enterprise.nav.reports")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.usage.workflows")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.companies.lastActivity")}</th>
                    <th class="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(company) => (
                      <tr class="border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                        <td class="px-5 py-4">
                          <button
                            type="button"
                            class="flex items-center gap-3 text-left"
                            onClick={() => openCompany(company)}
                          >
                            <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-[11px] font-semibold">
                              {initials(company.name)}
                            </span>
                            <span>
                              <span class="block text-[13px] font-medium text-v2-text-text-base">{company.name}</span>
                              <span class="mt-0.5 block text-[10px] capitalize text-v2-text-text-muted">
                                {company.role}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td class="px-4 py-4 text-[12px] text-v2-text-text-muted">
                          {company.country || language.t("cowork.enterprise.common.notAvailable")}
                        </td>
                        <td class="px-4 py-4">
                          <StatusBadge status={company.status} />
                        </td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{company.projects}</td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{company.files}</td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{company.reports}</td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{company.active_workflows}</td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {formatDate(company.last_activity, language.t("cowork.enterprise.companies.noActivity"))}
                        </td>
                        <td class="px-5 py-4 text-right">
                          <div class="flex justify-end gap-2">
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              icon={<IconV2 name="outline-square-arrow" size="small" />}
                              aria-label={language.t("cowork.enterprise.companies.open")}
                              title={language.t("cowork.enterprise.companies.open")}
                              onClick={() => openCompany(company)}
                            />
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              icon={<IconV2 name="edit" size="small" />}
                              aria-label={language.t("common.edit")}
                              title={language.t("common.edit")}
                              disabled={state.saving}
                              onClick={() => editCompany(company)}
                            />
                            <Show when={company.role === "owner"}>
                              <IconButtonV2
                                size="small"
                                variant="ghost"
                                class="text-v2-state-fg-danger"
                                icon={<IconV2 name="trash" size="small" />}
                                aria-label={language.t("common.delete")}
                                title={language.t("common.delete")}
                                disabled={state.deleting === company.id || state.saving}
                                onClick={() =>
                                  dialog.show(() => (
                                    <DialogCoworkDeleteCompany
                                      name={company.name}
                                      onDelete={() => void removeCompany(company)}
                                    />
                                  ))
                                }
                              />
                            </Show>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
    </div>
  )
}

function SummaryCard(props: { label: string; value: number }) {
  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base px-5 py-4">
      <div class="text-[11px] text-v2-text-text-muted">{props.label}</div>
      <div class="mt-1 text-[24px] font-semibold tracking-[-0.025em] tabular-nums">{props.value}</div>
    </div>
  )
}

function StatusBadge(props: { status: string }) {
  return (
    <span class="inline-flex rounded-md bg-v2-state-bg-success px-2 py-1 text-[10px] font-medium capitalize text-v2-state-fg-success">
      {props.status}
    </span>
  )
}

function EmptyState(props: { loading: boolean; text: string }) {
  return (
    <div class="grid min-h-56 place-items-center p-8 text-center text-[12px] text-v2-text-text-muted">
      {props.loading ? (
        <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
      ) : (
        props.text
      )}
    </div>
  )
}

function SearchIcon() {
  return (
    <svg
      class="size-4 shrink-0 text-v2-icon-icon-muted"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </svg>
  )
}

function initials(value: string) {
  return (
    value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((item) => item[0]?.toUpperCase())
      .join("") || "CO"
  )
}

function formatDate(value: string | null | undefined, fallback: string) {
  if (!value) return fallback
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return fallback
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date)
}
